'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

interface UseFontLoaderOptions {
  fontFamily?: string | null;
  variants?: string[];
}

/**
 * useFontLoader is a custom hook that handles dynamic font loading.
 * It loads the specified font family with given variants and returns the loading state.
 * 
 * @param fontFamily - The font family name to load
 * @param variants - Array of font variants to load (default: ["regular", "500", "600", "700"])
 * @returns Object with fontsLoaded boolean indicating if fonts are ready
 */
export function useFontLoader({
  fontFamily,
  variants = ["regular", "500", "600", "700"]
}: UseFontLoaderOptions) {
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    if (fontFamily && !fontsLoaded) {
      const loadFontAsync = async () => {
        try {
          await loadFont({
            family: fontFamily,
            variants,
          });

          // Wait for the font to be fully loaded
          await document.fonts.load(`16px ${fontFamily}`);

          // Only set fontsLoaded to true after the font is available
          setFontsLoaded(true);
        } catch (err) {
          console.error('Error loading fonts', err);
          Sentry.captureException(err);
          // Set to true even on error to prevent infinite loading
          setFontsLoaded(true);
        }
      };

      if (typeof window !== 'undefined' && fontFamily) {
        loadFontAsync();
      }
    }
  }, [fontFamily, fontsLoaded, variants]);

  // Reset fontsLoaded when fontFamily changes
  useEffect(() => {
    if (fontFamily) {
      setFontsLoaded(false);
    }
  }, [fontFamily]);

  return { fontsLoaded };
}

interface Props {
  family: string,
  variants: string[]
}

const loadFont = async ({ family, variants }: Props): Promise<boolean> => {
  const googleFontFamily = `${family}:${variants.join(",")}&display=swap`;
  const WebFont = (await import('webfontloader')).default;

  return new Promise((resolve, reject) => {
    WebFont.load({
      google: {
        families: [googleFontFamily],
      },
      active: () => {
        resolve(true);

      },
      inactive: () => {
        reject(false);
      },
    });
  });
};